import {
  Box,
  Stack,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { CHROME } from '@/designs-dev/loft/LoftDrill.fixtures';

const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

// How long a copied toast stays before auto-dismissing (matches the shrink bar).
export const TOAST_MS = 4600;

const KF = {
  toastIn: {
    '@keyframes loftToast': {
      from: { opacity: 0, transform: 'translateY(-10px) scale(0.97)' },
      to: { opacity: 1, transform: 'translateY(0) scale(1)' },
    },
  },
  shrink: {
    '@keyframes loftShrink': {
      from: { transform: 'scaleX(1)' },
      to: { transform: 'scaleX(0)' },
    },
  },
};

type CopiedToastProps = {
  accent: string; // tone colour (badge, progress bar)
  onClose: () => void;
  title?: string;
  containerSx?: SxProps<Theme>; // placement (absolute/fixed + offsets)
};

// The "copied to clipboard" confirmation used across Loft: a badge, the title,
// and a shrinking auto-dismiss bar. No command or subtext — the command is
// already shown on the chip the user clicked.
const CopiedToast = ({
  accent,
  onClose,
  title = 'Copied to clipboard',
  containerSx,
}: CopiedToastProps) => (
  <Box
    sx={{
      zIndex: 60,
      width: 352,
      bgcolor: CHROME.surface,
      border: `1px solid ${CHROME.lineCard}`,
      borderRadius: '13px',
      boxShadow: '0px 16px 32px -8px rgba(16,24,40,.24)',
      overflow: 'hidden',
      ...KF.toastIn,
      animation: `loftToast .22s ${EASE_OUT}`,
      ...containerSx,
    }}
  >
    <Stack
      sx={{ alignItems: 'center', p: '14px 15px 13px' }}
      direction='row'
      spacing={1.5}
    >
      <Box sx={{ color: accent, display: 'flex', flex: 'none' }}>
        <Icon icon='check-circle' fontSize={30} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          direction='row'
          spacing={1}
        >
          <Typography variant='body2' sx={{ color: CHROME.ink }}>
            {title}
          </Typography>
          <Box
            component='button'
            onClick={onClose}
            aria-label='Dismiss'
            sx={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              p: '2px',
              display: 'flex',
              color: 'rgba(0,0,0,.35)',
              '&:hover': { color: 'rgba(0,0,0,.7)' },
            }}
          >
            <Icon icon='circle-xmark' fontSize={15} />
          </Box>
        </Stack>
      </Box>
    </Stack>
    <Box sx={{ height: '3px', bgcolor: 'rgba(0,0,0,.06)', overflow: 'hidden' }}>
      <Box
        sx={{
          height: '100%',
          width: '100%',
          transformOrigin: 'left',
          bgcolor: accent,
          ...KF.shrink,
          animation: `loftShrink ${TOAST_MS}ms linear forwards`,
        }}
      />
    </Box>
  </Box>
);

export default CopiedToast;
